import { exactInteger } from '@/lib/exact'
import type { Block, Cell, ChipCell, Column, TableBlock } from '@/kova/spec/types'
import { ApiError } from '@/lib/api/errors'

import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'

/**
 * Risk › Typologies (`rk-typologies`), from `GET /v1/admin/risk/typologies`.
 *
 * # The corpus screen drew a rule the schema cannot hold
 *
 * It showed `structuring_pattern` with the outcome **`refuse_to_submit`**. The
 * table's CHECK constraint forbids it, and the endpoint refuses the filter with
 * the reasoning spelled out:
 *
 * > *"There is no refuse or freeze action: monitoring is detective, the
 * > strongest thing a typology may do is stop the instruction and put a human
 * > in front of it, and only the institution freezes."* — invariant 8.
 *
 * The invariant citation sits OUTSIDE the quotation marks rather than inside
 * them, where the backend has it, and that is deliberate — put it back and the
 * build fails. `scripts/check-invariants.sh` looks for a freeze CONTROL by
 * matching the verb followed by an open parenthesis, which is what a call looks
 * like. An English sentence that ends with that verb and then opens a
 * parenthetical citation is indistinguishable from one, so the prose has to
 * carry its citation some other way.
 *
 * **The check is right and does not change.** There is no freeze control in
 * this module, in the entitlement write controls, or anywhere else on this
 * console — the failure was a sentence, not a button. A rule that occasionally
 * catches prose is enormously better than one that occasionally misses an
 * affordance, and loosening it to accommodate a comment would trade a real
 * guarantee for a cosmetic one. Reword the comment.
 *
 * This is worth being precise about, because it looks like law 14 and is not.
 * Law 14 says risk OUTCOMES are a closed set of four. A typology's `action` is
 * a strict subset of **two** of them — `review` and `step_up`. The fixture's
 * chip was not a fifth outcome; it was one of the four drawn where the schema
 * forbids it, describing a monitoring rule that could refuse a customer's
 * instruction outright. No such rule can exist.
 *
 * # `mode` is read before `action`, and the sentence saying so is DERIVED
 *
 * Every seeded rule is in shadow, so `action` is what a rule **would** do if it
 * were promoted, never what it does — a shadow hit reaches the decision as an
 * annotation that cannot stop the chain. The backend derives that note from how
 * many rows can actually decide rather than asserting it, *"because a constant
 * saying so would keep saying it the day somebody promotes one — on the screen
 * whose whole job is to show that promotion happened"*. So this module renders
 * `mode_note` as sent and computes nothing of its own from it.
 *
 * # There is no "Fired 30d" column, and its absence is a fact worth rendering
 *
 * The corpus drew one with 188, 22, 41, 96 and 3 in it. A fire is recorded only
 * inside `authz_decisions.reasons` (JSONB), and no index on that table indexes
 * that column — so counting per typology means fetching every decision in the
 * window and detoasting its JSONB, which is the read migration 046 exists to
 * keep off that table. The fires are readable one decision at a time from
 * `GET /v1/admin/decisions/{ref}`. The screen says which route rather than
 * leaving a column blank.
 *
 * # A malformed rule is a row, not an error
 *
 * `problem` present means the authorisation engine cannot evaluate the rule: it
 * is in the table, reads as configured, and monitors nothing. The money path's
 * reader refuses the whole set when one row is malformed, which is right there
 * and wrong here — *"the unevaluatable row is exactly what an operator opened
 * this screen to find."*
 *
 * # `effective_from` is a DEPLOY timestamp, not a policy date
 *
 * Nothing in Go writes this table; the only INSERT is migration 016's seed, and
 * editing a monitoring rule by hand is the sanctioned way it is done. So there
 * is no audit trail and no `published_by` for any row, and `effective_from` on
 * a seeded rule is the instant that migration ran in this environment. A
 * compliance reviewer must not read it as "in force since", so the column is
 * labelled for what it is and the provenance note is carried verbatim.
 *
 * # No promote control, and that is not an omission
 *
 * A promote button here would let the screen that READS these rules change what
 * the authorisation seam does, from a surface with no audit trail behind it.
 * The seam has one method and it is a query.
 */

export const RISK_TYPOLOGIES_PATH = '/risk/typologies'

const SOURCE = 'postgres · risk_typologies (GET /v1/admin/risk/typologies)'
const AUTHORITY = 'operational'

interface Typology {
  readonly id: number | null
  readonly code: string
  readonly version: number | null
  readonly kind: string
  /** Whether the rule can decide anything. Read BEFORE `action`. */
  readonly mode: string
  readonly action: string
  /** Empty means every segment. Never absent — the two mean opposite things. */
  readonly segments: readonly string[]
  /** Present exactly when an amount is served. */
  readonly currency: string
  readonly countThreshold: number | null
  readonly amountThresholdMinor: number | null
  readonly windowDays: number | null
  readonly bandBps: number | null
  readonly effectiveFrom: string
  readonly effectiveTo: string
  /** Judged at the response's `as_of`, not at this console's clock. */
  readonly live: boolean | null
  /** Why the engine cannot evaluate this rule. Empty when it can. */
  readonly problem: string
}

function readTypology(raw: unknown): Typology | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const code = str(o['code'])
  if (code === '') return null
  return {
    id: int(o['id']),
    code,
    version: int(o['version']),
    kind: str(o['kind']),
    mode: str(o['mode']),
    action: str(o['action']),
    segments: Array.isArray(o['segments'])
      ? o['segments'].filter((s): s is string => typeof s === 'string')
      : [],
    currency: str(o['currency']),
    countThreshold: int(o['count_threshold']),
    amountThresholdMinor: int(o['amount_threshold_minor']),
    windowDays: int(o['window_days']),
    bandBps: int(o['band_bps']),
    effectiveFrom: str(o['effective_from']),
    effectiveTo: str(o['effective_to']),
    live: typeof o['live'] === 'boolean' ? o['live'] : null,
    problem: str(o['problem']),
  }
}

/**
 * The columns, and the one that is deliberately not among them.
 *
 * No "Fired 30d". `Mode` sits BEFORE `Would do` because that is the order the
 * response asks for them to be read in, and a console that put the action first
 * would train the opposite habit.
 */
const COLS: readonly Column[] = [
  ['Rule', 'm', 'minmax(200px,1.4fr)'],
  ['Version', 'm', '90px'],
  ['Kind', 'm', '150px'],
  ['Mode', 'c', '150px'],
  ['Would do', 'c', '140px'],
  ['Threshold', 'x', 'minmax(190px,1.3fr)'],
  ['Applies to', 'x', 'minmax(150px,1.1fr)'],
  ['In the table since', 'm', '150px'],
  ['Live at read', 'c', '130px'],
]

/**
 * Whether the rule can decide anything, which is the first question.
 *
 * A row carrying `problem` decides nothing whatever its mode says, so that is
 * checked first: a malformed rule in `enforce` reads as the most consequential
 * row on the screen and is in fact the most inert.
 */
function modeCell(t: Typology): ChipCell {
  if (t.problem !== '') return ['BROKEN — decides nothing', 'r']
  switch (t.mode) {
    case '': return ['— not stated', 'r']
    case 'shadow': return ['shadow — cannot decide', 'n']
    case 'enforce': return ['enforce — can decide', 'a']
    default: return [t.mode, 'n']
  }
}

/**
 * What the rule would do, and the rendering the schema forbids.
 *
 * `refuse_to_submit` and `freeze` cannot be stored in this column. If one ever
 * arrives it is a row nothing should have been able to write, and rendering it
 * as an ordinary outcome would present a monitoring rule that refuses a
 * customer's instruction outright as a normal configuration.
 */
function actionCell(t: Typology): ChipCell {
  switch (t.action) {
    case '': return ['— not stated', 'r']
    case 'review': return ['would review', 'a']
    case 'step_up': return ['would step up', 'a']
    default:
      return [`${t.action} — IMPOSSIBLE, the schema forbids this`, 'r']
  }
}

/**
 * The thresholds this kind actually reads, and the amount that is withheld.
 *
 * Law 8's one genuinely fragile pairing on this surface: a malformed rule with
 * an amount and no currency is a real state of the table, and it is precisely
 * what `problem` names. The backend withholds the amount rather than sending it
 * currency-less; if one arrives anyway, it is not drawn.
 */
function thresholdCell(t: Typology): Cell {
  const parts: string[] = []
  if (t.countThreshold !== null) parts.push(`${String(t.countThreshold)} events`)
  if (t.amountThresholdMinor !== null) {
    parts.push(t.currency === ''
      ? 'an amount arrived with NO CURRENCY and is not shown'
      : `${String(t.amountThresholdMinor)} minor · ${t.currency}`)
  }
  if (t.windowDays !== null) parts.push(`${String(t.windowDays)}d window`)
  if (t.bandBps !== null) parts.push(`${String(t.bandBps)} bps band`)
  return parts.length === 0 ? '— none served for this kind' : parts.join(' · ')
}

/**
 * Which segments the rule applies to.
 *
 * An EMPTY list means every segment, and the backend never omits the field
 * precisely so a console cannot confuse the two. Rendering an empty list as a
 * blank cell would make the widest rule on the screen look like the narrowest.
 */
function segmentCell(t: Typology): Cell {
  return t.segments.length === 0 ? 'every segment' : t.segments.join(', ')
}

function liveCell(t: Typology): ChipCell {
  if (t.live === null) return ['— not stated', 'r']
  return t.live ? ['in its window', 'g'] : ['outside its window', 'n']
}

function row(t: Typology): readonly Cell[] {
  return [
    t.code,
    t.version === null ? '—' : `v${String(t.version)}`,
    t.kind === '' ? '— not stated' : t.kind,
    modeCell(t),
    actionCell(t),
    thresholdCell(t),
    segmentCell(t),
    t.effectiveFrom === '' ? '—' : day(t.effectiveFrom),
    liveCell(t),
  ]
}

/** The five notes the response carries, rendered as the backend wrote them. */
const REQUIRED_NOTES: readonly (readonly [key: string, kicker: string, tone: 'a' | 'n' | 'r'])[] = [
  ['mode_note', 'Read mode before action', 'a'],
  ['provenance_note', 'Nothing in Go writes this table', 'a'],
  ['malformed_note', 'A rule the engine cannot evaluate', 'n'],
  ['fired_note', 'There is no “Fired 30d” count', 'n'],
  ['control_note', 'Read only — there is no promote', 'n'],
]

export function composeTypologies(payload: Record<string, unknown>, readAt: Date): ScreenData {
  const raw = Array.isArray(payload['typologies'])
    ? (payload['typologies'] as readonly unknown[])
    : []
  const rows: Typology[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readTypology(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const truncated = payload['truncated'] === true
  const asOf = str(payload['as_of'])
  const broken = rows.filter((t) => t.problem !== '').length
  const impossible = rows.filter(
    (t) => t.action !== '' && t.action !== 'review' && t.action !== 'step_up',
  )

  const table: TableBlock = {
    t: 'table',
    title: 'Monitoring rules',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} rule(s)${truncated ? ' — and more beyond this page' : ''}${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2: asOf === ''
      ? '“Live at read” is judged at an instant the response did not state, so it is unanchored — a live flag is an answer about a moment and is unreadable without the moment.'
      : `“Live at read” is judged at ${asOf}, the instant the backend stated, and not at this console’s clock.`,
  }

  const blocks: Block[] = [table]

  if (impossible.length > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'A rule that should not exist',
      title: `${String(impossible.length)} rule(s) carry an action the table's CHECK constraint forbids`,
      body: `${impossible.map((t) => `${t.code} → ${t.action}`).join(', ')}. A typology may only review or step up: monitoring is detective, and the strongest thing one may do is stop the instruction and put a human in front of it. Only the institution freezes. A row holding anything else was written by something that bypassed the constraint, and it is reported rather than rendered as an ordinary configuration.`,
      ref: 'risk_typologies action CHECK · invariant 8',
    })
  }

  if (broken > 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Configured and monitoring nothing',
      title: `${String(broken)} rule(s) cannot be evaluated by the authorisation engine`,
      body: `${rows.filter((t) => t.problem !== '').map((t) => `${t.code}: ${t.problem}`).join(' · ')}. These rows are in the table and read as configured. They decide nothing, whatever their mode says — which is why they are shown here rather than being allowed to fail the whole catalogue, as the money path's reader would.`,
      ref: 'problem is served on the row',
    })
  }

  if (truncated) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Not the whole catalogue',
      title: 'This page is cut short, and the backend says so',
      body: 'A catalogue an operator reads as complete and is not is the one wrong answer this screen can give: it would say a monitoring programme covers less than it does. Narrow the query rather than reading the last row as the last rule.',
      ref: 'truncated is served on every response',
    })
  }

  for (const [key, kicker, tone] of REQUIRED_NOTES) {
    const body = str(payload[key])
    if (body !== '') {
      blocks.push({ t: 'note', tone, kicker, title: 'From the backend, verbatim', body, ref: key })
    }
  }

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} typology row(s) could not be read`,
      body: 'Counted rather than dropped silently. A monitoring catalogue quietly one rule shorter than the table understates what is being watched, which is the direction that matters.',
      ref: 'the count is the finding',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Empty',
      title: 'No monitoring rule matched, and an empty catalogue is not a safe state',
      body: 'With no typologies an authorisation resolves on entitlements and limits alone. That is a policy decision rather than a default, and it is not the same as a programme that is watching and finding nothing.',
      ref: 'an empty catalogue is a policy state',
    })
  }

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (truncated) missing.push('the rest of the catalogue beyond this page')
  // Always. The design's column cannot be filled from any read on this surface,
  // and naming where the fires ARE readable is more use than a blank column.
  missing.push('how often each rule fired — readable only one decision at a time from GET /v1/admin/decisions/{ref}')
  missing.push('who published a rule and when — nothing in Go writes this table, so no row has an author')

  const provenance: DataSource = {
    kind: 'partial', source: SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing,
  }
  const subject = `risk_typologies · ${String(rows.length)} rule(s) read`
  return { blocks, provenance, subject }
}

export const loadTypologies: ScreenLoader = async (api) => {
  let payload: unknown
  try {
    payload = await api.get<unknown>(RISK_TYPOLOGIES_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The typology catalogue answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The typology catalogue returned a shape this console does not know.', null)
  }
  return composeTypologies(payload as Record<string, unknown>, new Date())
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The typology catalogue could not be read',
      body: `${reason} Nothing is drawn. This is NOT an empty catalogue and it is not a statement that nothing is being monitored — which rules this deployment runs is unknown from here, and "no rules" is itself a policy state rather than a neutral one.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function day(stamp: string): string {
  const t = stamp.indexOf('T')
  return t === -1 ? stamp : stamp.slice(0, t)
}
